import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import CategoryIcon from "@mui/icons-material/Category";
import StoreIcon from "@mui/icons-material/Store";

import { StyledCard } from "../common/StyledCard";
import { InventoryItemResponseDto } from "../../shared/dtos/inventoryItem/InventoryItemResponseDto";
import { formatCurrency } from "../../utils/appUtils";

interface InventoryItemCardProps {
  inventory: InventoryItemResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const InventoryItemCard: React.FC<InventoryItemCardProps> = ({
  inventory,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ maxWidth: "75%" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {inventory.name}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 0.5, alignItems: "center" }}
            >
              <Chip
                label={inventory.itemCode}
                size="small"
                color="secondary"
                variant="outlined"
                icon={<Inventory2Icon fontSize="small" />}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              />
            </Stack>
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Item">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Item">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Body Details */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          {inventory.category && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <CategoryIcon fontSize="small" sx={{ color: "action.active" }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {inventory.category}
              </Typography>
            </Stack>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                On Hand
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                <span style={{ fontSize: "0.8em", fontWeight: "normal" }}>
                  {inventory.unitOfMeasure}
                </span>
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Unit Cost
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {formatCurrency(inventory.unitCost)}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: "0.85rem",
              fontStyle: "italic",
            }}
          >
            {inventory.description || "No description provided."}
          </Typography>
        </Stack>

        {/* Footer Supplier */}
        <Box
          sx={{ mt: 2, pt: 1, borderTop: "1px dashed", borderColor: "divider" }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <StoreIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography
              variant="caption"
              sx={{
                color: inventory.preferredSupplierName
                  ? "text.primary"
                  : "text.disabled",
              }}
            >
              {inventory.preferredSupplierName || "No Supplier Assigned"}
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default InventoryItemCard;
